/**
 * ******************************************************
 * Copyright (C) 2021 VMware, Inc. All rights reserved.
 * *******************************************************
 *
 * @format
 */

import Logger from "../../../../core/libs/logger";
import { BCR_CONST } from "./html5MMR-consts";

export class BCRHelper {
   private whitelistPattern: string;
   private navWhitelistPattern: string;

   private whiteListURLs: Set<string> = new Set();
   private allUrlsWhitelisted: boolean = false;


   /**
    * Cert errors referenced from CEF source code
    * https://github.com/chromium/chromium/blob/08f4b00a87c2e15a7517c88c12a394d7e61546c8/net/base/net_error_list.h#L457
    *
    * SSL_PINNED_KEY_NOT_IN_CERT_CHAIN, -150
    * CERT_COMMON_NAME_INVALID, -200
    * CERT_DATE_INVALID,-201
    * CERT_AUTHORITY_INVALID, -202
    * CERT_CONTAINS_ERRORS, -203
    * CERT_NO_REVOCATION_MECHANISM, -204
    * CERT_UNABLE_TO_CHECK_REVOCATION, -205
    * CERT_REVOKED, -206
    * CERT_INVALID, -207
    * CERT_WEAK_SIGNATURE_ALGORITHM, -208
    * CERT_NON_UNIQUE_NAME, -210
    * CERT_WEAK_KEY, -211
    * CERT_NAME_CONSTRAINT_VIOLATION, -212
    * CERT_VALIDITY_TOO_LONG, -213
    * CERTIFICATE_TRANSPARENCY_REQUIRED, -214
    * CERT_SYMANTEC_LEGACY, -215
    * CERT_KNOWN_INTERCEPTION_BLOCKED, -217
    */
   static certErrorList: Set<number> = new Set([-150, -200, -201, -202, -203, -204, -205, -206, -207, -208, -210, -211, -212, -213, -214, -215, -217]);

   public constructor() {
      this.reset();
   }

   public set whitelist(pattern: string) {
      this.whitelistPattern = pattern;
   }

   public set navWhitelist(pattern: string) {
      this.navWhitelistPattern = pattern;
   }

   public getUrlsPattern = () : Array<string> => {
      return  (this.allUrlsWhitelisted)? [BCR_CONST.ALL_MATCH]: Array.from(this.whiteListURLs);
   }

   /**
    * Clear all buffer in the class
    * which includes parsed urls and flags.
    */
   private reset = () => {
      this.whiteListURLs.clear();
      this.allUrlsWhitelisted = false;
   };

   /**
    * Parse the pattern(s) sent by server
    * allUrlsWhitelisted = true,  if <all_urls> is present in whitelistPattern or navWhitelistPattern.
    *
    * If<all_urls> is not present in pattern, parse the pattern
    * and fill the set whiteListURLs.
    *
    * @param : none
    */
   public parseUrls = () => {
      this.reset();

      this.parseWhiteList(this.whitelistPattern);
      if (!this.allUrlsWhitelisted) {
         this.parseWhiteList(this.navWhitelistPattern);
      }

      if (this.allUrlsWhitelisted) {
         Logger.info("All Urls are whitelisted.", Logger.BCR);
      } else {
         var output = this.whiteListURLs.size > 0 ? JSON.stringify(Array.from(this.whiteListURLs), null, 1) : "null";
         output = output.replace(/\n|\r/g, "");
         Logger.info("Whitelisted URls are : " + output, Logger.BCR);
      }
   };

   /**
    * Parse the pattern(s) sent by server.
    * if <all_urls> is present in the pattern, it means any of the website is whitelisted.
    * In above case keep the result in a boolean flag and return true for any url.
    *
    * If <all_urls> is not present, we may not have other patterns, separated by ","
    * In that case parse the "," separated urls and keep it inside an array.
    * The parsed url can contain wildcard "*" which means 0 - multiple chars.
    *
    * @param {string} pattern : Whitelisted pattern sent from server
    * @returns boolean - True if <all_urls> found in the pattern
    */
   private parseWhiteList = (
      pattern: string //In
   ): boolean => {
      try {
         if (!pattern || pattern.length === 0) {
            return false;
         }

         let allMatchFound: boolean = false;
         // Remove brackets []
         let input = pattern.slice(1, -1).trim();
         //if only [] is passed as input return;
         if (input.length === 0) {
            return false;
         }

         let allURLs = input.split(",");
         if (!allURLs) {
            return false;
         }

         for (let index = 0; index < allURLs.length; index++) {
            //Remove quotes from first and last position.
            let temp = allURLs[index].slice(1, -1).trim();
            if (temp === BCR_CONST.ALL_MATCH) {
               this.allUrlsWhitelisted = true;
               this.whiteListURLs.clear();
               break;
            } else if (temp && temp.length > 0 && temp.length < BCR_CONST.MAX_URL_LEN) {
               this.whiteListURLs.add(temp);
            }
         }
         return allMatchFound;
      } catch (e) {
         this.whiteListURLs.clear();
         Logger.error("Parsing error inside parseWhiteList. " + (e as Error).message, Logger.BCR);
         return false;
      }
   };

   /**
    * Match an Url and a pattern.
    * Pattern Should follow - https://developer.chrome.com/docs/extensions/mv3/match_patterns/
    * Using Dynamic Programming to find a match.
    * Logic copied from Horizon Client code, Link : https://tinyurl.com/2x2zsvpu
    *
    * @param {string} inputUrl , {string} whiteListPattern
    * @returns boolean
    */
   private matchPattern = (inputUrl: string, whiteListPattern: string): boolean => {
      if (inputUrl === whiteListPattern) {
         return true;
      }

      let urlLen = inputUrl.length;
      let pattrenLength = whiteListPattern.length;

      //Init array of row = urlLen+1, col = patternLength+1, fill all with false
      let matchTable: boolean[][] = new Array(urlLen + 1)
         .fill(false)
         .map(() => new Array(pattrenLength + 1).fill(false));
      matchTable[0][0] = true;

      for (let col = 1; col < pattrenLength + 1; col++) {
         if (whiteListPattern[col - 1] == "*") {
            matchTable[0][col] = matchTable[0][col - 1];
         }
      }

      for (let row = 1; row < urlLen + 1; row++) {
         for (let col = 1; col < pattrenLength + 1; col++) {
            if (whiteListPattern[col - 1] == "*") {
               matchTable[row][col] = matchTable[row - 1][col] || matchTable[row][col - 1];
            }

            if (inputUrl[row - 1] == whiteListPattern[col - 1]) {
               matchTable[row][col] = matchTable[row - 1][col - 1];
            }
         }
      }
      let match = matchTable[urlLen][pattrenLength];
      return match;
   };

   /**
    * Check in case url matches with any of the pattern inside urlPattern
    *
    * @param {string} url
    * @returns boolean
    */
   private isURLAllowed = (url: string): boolean => {
      if (this.whiteListURLs.size === 0) {
         return false;
      }

      //If exact match found
      if (this.whiteListURLs.has(url)) {
         return true;
      }

      //Look for pattern match and compare with each item.
      for (let currentPattern of this.whiteListURLs) {
         if (this.matchPattern(url, currentPattern)) {
            Logger.info("url: " + url + " matched the pattern: " + currentPattern, Logger.BCR);
            return true;
         }
      }
      return false;
   };

   /**
    * If WhitelistUrl have <all_urls>
    * it means url is white listed.
    *
    * Else check whether the Url matched with any of the URL/pattern present
    * inside whiteListURLs.
    *
    * @param {string} url
    * @returns boolean
    */
   public isUrlWhiteListed = (url: string): boolean => {
      if (this.allUrlsWhitelisted) {
         return true;
      } else if (this.isURLAllowed(url)) {
         return true;
      }
      return false;
   };

}

/**
 * This const class is used to store JS and HTML code that is injected
 * into webview. Webview has a known issue where it cannot find files from a
 * relative path, so a work around is to use the code as a string instead of keeping
 * it in its own file.
 */
export const BCR_INJECTED_CODE = {
   BCR_TITLE_INJECTION_SCRIPT(instanceId:string): string { return `(${bcrTitleInjectionScript})(${instanceId});`},
   ABORT_ERROR_FLAG(url: string, reason: string, code: number): string {
      return `<html><head><title>Page failed to load</title></head>
      <body bgcolor=\"white\"><h2>Page failed to load.</h2>
      URL:  ${url} <br/>Error:  ${reason} (${code})`;
   }
};

/**
 * JS code that is injected into the webview's webpage
 * This script is vanilla JS since the guest page it is being
 * injected into might not support TS or our custom classes.
 */
export function bcrTitleInjectionScript(instanceId) {
   //prevent double injection
   var bcrContentScript = bcrContentScript || function() {
      var embedder = null;

      //Send message to port connection with website info
      var postTitle = function (title) {
         console.log("<bcr> received title change, sending title: " + title + "to webview with ID: " + instanceId);
         var data = {
            type: "tabTitle",
            tabInstanceId: instanceId,
            title: title,
            url: window.location.href
         };
         embedder.postMessage(data);
      };

      //Open a connection with port name BCR
      var bindEmbedder = function () {
         embedder = chrome.runtime.connect({name: "BCR"});
      };

      //Create MutationObserver for title element if it exists and send initial title
      var init = function () {
         console.log("<bcr> Initializing injected script with MutationObservers on a title element for WebView ID:" + instanceId);
         bindEmbedder();
         let titleElement = document.querySelector("title");
         if (titleElement) {
            console.log("<bcr> title exists, adding MutationObserver");
            new MutationObserver(function (mutations) {
               if(mutations[0].type === "childList"){
                  postTitle(mutations[0].target.firstChild.nodeValue);
               }
            }).observe(document.querySelector("title"), {
               subtree: true,
               characterData: true,
               childList: true,
            });
            postTitle(document.title);
         } else {
            console.log("<bcr> title element does not exists");
         }
      }

      //Run init when DOM has loaded or now if its already loaded
      if (document.readyState === 'loading') {
         document.addEventListener('DOMContentLoaded', init);
      } else {
         init();
      }
   }();
}
